import React, { Component, useEffect, useMemo, useState } from 'react';
import { Typography, Grid } from '@material-ui/core';
import styled from 'styled-components';
import { connect, useDispatch, useSelector } from 'react-redux';
import cx from 'classnames';
import { getFormValues } from 'redux-form';
import { PageWrapper, CarCard, BannerSlider, ScrollableBlock } from '../../components';
import { UpdateCreditCardModal } from '../../components/Modals';
import { RecentlyAddedSlider } from './components';
import './index.scss';
import {
  listVehicles,
  subscribe,
  unsubscribe,
  modalsToggle,
  getNextAuction,
  vehiclesSetPage,
  getCurrentAuction
} from '../../actions';
import { roles, USER_STATUSES, DISABLE_REASONS, VEHICLES_PER_PAGE } from '../../constants';
import HomeFilters from './Filters';
import VehiclesPagination from './VehiclesPagination';
import { VehicleSidePanel } from '../../components/VehicleSidePanel/VehicleSidePanel';
import { VehicleSidePanelMobile } from '../../components/VehicleSidePanel';
import { openPanel } from '../../services/sidePanel/sidePanel';
import { useGetLiveAuctionVehiclesQuery } from 'services/api/live-auction';
import { useGetSidePanelBidsQuery } from 'services/api/my-bids';

const GridWrapper = styled(Grid)`
  display: ${({ gridView }) => (gridView ? 'grid' : 'flex')};
  grid-template-columns: repeat(4, 1fr);
  grid-template-rows: ${({ rows, rowWidth }) => `repeat(${rows}, ${rowWidth}px)`};

  @media (max-width: 1023px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 567px) {
    display: flex;
  }
`;

const PageContainer = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
`;

const GridItem = styled(Grid)`
  min-width: 100%;
  max-width: 100%;
  z-index: ${({ index }) => 50 - index};
`;

const ContentWrapper = styled.div`
  position: relative;
  flex-grow: 1;
`;

const SidePanelWrapper = styled.div`
  position: absolute;
  min-width: 256px;
  max-width: 256px;
  right: 0;
  top: 0;
  bottom: 0;
  overflow-y: scroll;
  height: calc(100% + 24px + 24px);
  margin-top: -24px;
  ${'' /* 
  ::-webkit-scrollbar {
    width: 1em;
  }

  ::-webkit-scrollbar-track {
    box-shadow: inset 0 0 6px rgba(0, 0, 0, 0.3);
  } */}

  @media (max-width: 1280px) {
    display: none;
  }
`;

const getWidth = () =>
  Math.max(
    document.body.scrollWidth,
    document.documentElement.scrollWidth,
    document.body.offsetWidth,
    document.documentElement.offsetWidth,
    document.documentElement.clientWidth
  );

const LiveAuctionFC = () => {
  const dispatch = useDispatch();

  const user = useSelector(state => state?.user?.user);

  const { page, perPage, filters } = useSelector(state => state.liveAuctions);
  const { data, isFetching: vehiclesFetching, isLoading: vehiclesLoading } = useGetLiveAuctionVehiclesQuery({
    limit: perPage,
    offset: (page - 1) * perPage,
    ...filters
  });

  const { data: recentlyAddedData, isLoading: recentlyAddedLoading } = useGetLiveAuctionVehiclesQuery({
    limit: 24,
    offset: 0
  });

  const { data: myBids } = useGetSidePanelBidsQuery(undefined, { skip: user?.role !== 'buyer' });

  const [listView, setListView] = useState(false);
  // const [viewChanged, setViewChanged] = useState(false);

  const vehicles = useMemo(() => data?.rows?.ids?.map(id => data?.rows?.entities?.[id]) ?? [], [data]);
  const vehiclesCount = useMemo(() => data?.count ?? 0, [data]);

  const recentlyAddedVehicles = useMemo(() => Object.values(recentlyAddedData?.rows?.entities ?? []), [
    recentlyAddedData
  ]);

  const auctionLoading = useSelector(state => state.auctions.loading);

  const changeToGrid = () => {
    setListView(false);
  };

  const changeToList = () => {
    setListView(true);
  };

  useEffect(() => {
    const isBuyer = user && user.role === roles.BUYER;
    const isDisabledByCard =
      user && user.status === USER_STATUSES.DISABLED && user.disable_reason === DISABLE_REASONS.CARD;

    if (isBuyer && isDisabledByCard) {
      dispatch(modalsToggle('updateCreditCardModal'));
    }
  }, [dispatch, user]);

  useEffect(() => {
    dispatch(getNextAuction().request);
    dispatch(getCurrentAuction().request);
  }, [dispatch]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [page]);

  const hasBids = myBids && myBids.count > 0;
  const isFiltersEmpty =
    Object.keys(filters) === 0 ||
    (filters.make === '0' &&
      filters.model === '0' &&
      filters.seller === '0' &&
      filters.purchase === '0' &&
      !filters.minRange &&
      !filters.maxRange &&
      filters.region === '0');
  const isSeller = user && user.role === roles.SELLER;
  const isBuyer = user && user.role === roles.BUYER;
  const isLoading = vehiclesFetching || auctionLoading;
  const isAuctionShuffled = vehicles.find(item => +item.end_time_spacing > 0);

  return (
    <PageContainer>
      {!isSeller && <HomeFilters />}
      <ContentWrapper>
        <div className={cx('vehicles-holder', !hasBids ? 'no-bids' : '')}>
          <PageWrapper>
            <BannerSlider />
            {!recentlyAddedLoading && !vehiclesFetching && (recentlyAddedVehicles || []).length > 0 && (
              <>
                {!isAuctionShuffled && (vehicles || []).length > 5 && <RecentlyAddedSlider items={vehicles} />}
                <Grid container justify="space-between" className="all-auctions">
                  <ScrollableBlock handleScroll={() => {}}>
                    <Typography
                      gutterBottom
                      variant="display1"
                      // ref={node => {
                      //   this.stickyBlock = node;
                      // }}
                    >
                      {`All Vehicles in Auction Order (${vehiclesCount})`}
                    </Typography>
                  </ScrollableBlock>
                  <Grid item className="view-changer">
                    <i
                      className={cx('material-icons', {
                        active: !listView
                      })}
                      onClick={changeToGrid}
                      onKeyDown={changeToGrid}
                      tabIndex="0"
                      role="button"
                    >
                      view_module
                    </i>
                    <i
                      className={cx('material-icons', {
                        active: listView
                      })}
                      onClick={changeToList}
                      onKeyDown={changeToList}
                      tabIndex="0"
                      role="button"
                    >
                      view_stream
                    </i>
                  </Grid>
                </Grid>
                {!isLoading && (
                  <GridWrapper
                    gridView={!listView}
                    rows={Math.ceil(vehicles.length / 4)}
                    rowWidth={user.role === 'buyer' ? 450 : 420}
                    container
                    spacing={24}
                  >
                    {vehicles.map((item, i) => (
                      <GridItem
                        item
                        xs={12}
                        sm={listView ? 12 : 6}
                        lg={listView ? 12 : 3}
                        md={4}
                        key={i}
                        index={i}
                      >
                        <CarCard listView={listView} vehicle={item} withHover />
                      </GridItem>
                    ))}
                  </GridWrapper>
                )}
                <VehicleSidePanelMobile />
                <VehiclesPagination page={+page} perPage={perPage} count={+vehiclesCount} />
              </>
            )}
            {!isLoading &&
              vehiclesCount > 0 &&
              (vehicles || []).length === 0 &&
              +page > Math.ceil(vehiclesCount / perPage) && (
                <p className="text-center">
                  The number of listed cars has changed.
                  <br />
                  {`Please go back to page ${+page - 1}.`}
                  <VehiclesPagination page={+page} perPage={perPage} count={+vehiclesCount} />
                </p>
              )}
            {!isLoading && vehiclesCount === 0 && (vehicles || []).length === 0 && isFiltersEmpty && (
              <p className="text-center">Fresh vehicles will be uploaded soon!</p>
            )}
            {!isLoading && (vehicles || []).length === 0 && !isFiltersEmpty && (
              <p className="text-center">
                No vehicles found that meet your criteria.
                <br />
                Please clear any filters and try again.
              </p>
            )}
            {isLoading && <p className="text-center">Loading vehicles...</p>}
          </PageWrapper>
        </div>
        <SidePanelWrapper hidden={!hasBids || vehiclesLoading || vehiclesFetching}>
          <VehicleSidePanel />
        </SidePanelWrapper>
      </ContentWrapper>

      {isBuyer && <UpdateCreditCardModal user={user} />}
    </PageContainer>
  );
};

export default LiveAuctionFC;
